import styles from './WorkspaceSelector.module.scss';
import { Text } from './typography';
import { Icon } from '@mui/material';
import { useNavigate, matchPath } from 'react-router-dom';
import { paths } from '../constants/strings';
import { useState, useMemo } from 'react';
import defaultLogo from '../assets/workspace_image.png';
import Searchbar from './Searchbar';
import { getRoute } from '../constants/strings';
import { useCurrentUser } from '../providers/UserProvider';
import { useWorkspace } from '../providers/WorkspaceProvider';
import { useModal } from '../providers/ModalProvider';
import { modals } from '../providers/modals';
import { useTranslation } from 'react-i18next';
import { keys } from '../utilities/translator/translation_keys';
import { useWorkspaceMutations } from '../hooks/addUserToWorkspace';
import { useCompanyMutations } from '../hooks/company/update_company';

export default function WorkspaceSelector({ show, setShow }) {
  const navigate = useNavigate();
  const { isAdmin, refetchUser } = useCurrentUser();
  const { user } = useCurrentUser();
  const {
    workspace: w,
    availableWorkspaces,
    inWorkspaces,
    notInWorkspaces,
    allWorkspaces,
  } = useWorkspace();
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { addUser } = useWorkspaceMutations();
  const { updateUser } = useCompanyMutations();

  const [search, setSearch] = useState('');

  const workspace = useMemo(() => w, [w]);

  const filteredOptions = useMemo(() => {
    const userWorkspaces = isAdmin ? allWorkspaces : availableWorkspaces;
    return search?.length
      ? userWorkspaces?.filter((workspace) =>
          workspace.title.toLowerCase().includes(search.toLowerCase()),
        )
      : userWorkspaces?.filter((w) => w.id !== workspace?.id);
  }, [allWorkspaces, availableWorkspaces, isAdmin, search, workspace?.id]);

  const updateWorkspaceId = (newWorkspaceId) => {
    const match = matchPath(
      '/workspace/:workspaceId/*',
      window.location.pathname,
    );
    if (match) {
      const restOfPath = window.location.pathname.split('/').slice(3).join('/');
      navigate(
        `/workspace/${newWorkspaceId}/${restOfPath}${window.location.search}`,
      );
    } else {
      updateUser({
        lastWorkspaceId: newWorkspaceId,
      }).then(() => refetchUser());
    }
  };

  return show ? (
    <div className={show ? styles.show : styles.hide}>
      <div className={styles.container}>
        <div className={styles.modalHeader}>
          <Text color="accentPrimary" size="md" noMargin weight="bold">
            {workspace?.title}
          </Text>
          <div className={styles.optionsContainer}>
            <Icon
              className={styles.closeIcon}
              onClick={() => {
                navigate(getRoute(workspace?.id, paths.workspaceSettings));
                setShow(false);
              }}
            >
              settings
            </Icon>
            <Icon className={styles.closeIcon} onClick={() => setShow(false)}>
              close
            </Icon>
          </div>
        </div>
        <div>
          <Searchbar
            value={search}
            onChange={(value) => setSearch(value)}
            debounce={false}
          />
          <div
            className={
              isAdmin ? styles.dropdownContainer : styles.dropdownContainerLong
            }
          >
            {filteredOptions?.map((workspace) => (
              <div
                key={workspace.id}
                className={styles.dropdown}
                onClick={async () => {
                  if (
                    notInWorkspaces.some((w) => w.id === workspace.id) &&
                    isAdmin
                  ) {
                    await addUser({
                      userId: user.id,
                      workspaceId: workspace.id,
                      accessLevel: 'ADMIN',
                    });
                  }
                  updateWorkspaceId(workspace.id);
                  setShow(false);
                }}
              >
                <div
                  className={
                    inWorkspaces?.some((w) => w.id === workspace.id)
                      ? styles.workspaceCard
                      : styles.availableWorkspaceCard
                  }
                >
                  <img
                    className={styles.logo}
                    src={workspace.logoUrl ? workspace.logoUrl : defaultLogo}
                    alt="default"
                  ></img>
                  <Text noMargin weight="semiBold" size="sm" truncate>
                    {workspace.title}
                  </Text>
                </div>
                <Icon
                  sx={{
                    fontSize: '0.75rem',
                    color:
                      workspace?.status === 'Active' ? '#28a745' : '#e9222c',
                  }}
                  className={styles.statusButton}
                >
                  circle
                </Icon>
              </div>
            ))}
          </div>
        </div>
        <div
          className={isAdmin ? styles.buttonContainer : styles.hide}
          onClick={() => {
            setShow(false);
            openModal({ modalName: modals.createWorkspace });
          }}
        >
          <Icon className={styles.plusIcon}>add</Icon>
          <Text
            className={styles.buttonText}
            color="accentPrimary"
            size="md"
            noMargin
            weight="bold"
            textAlign="center"
          >
            {t(keys.action.CREATE_VARIABLE, {
              variable: t(keys.common.WORKSPACE),
            })}
          </Text>
        </div>
      </div>
    </div>
  ) : null;
}
